import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '../../core/services/user.service';
import { LoaderService } from '../../shared/components/loader/loader.service';
import { HeaderComponent } from '../../shared/layout/app/header/header.component';
import { LeftSideNavComponent } from '../../shared/layout/app/left-menu/left-side-nav.component';
import { BaseResponse } from '../../shared/models/base-response.model';
import { ProfileBllService } from '../profile/profile-bll.service';
import { UserEventStatusRespModel } from '../profile/profile.model';

@Component({
  selector: 'app-virtual-event',
  imports: [LeftSideNavComponent, HeaderComponent, CommonModule],
  templateUrl: './virtual-event.component.html',
  styleUrls: ['./virtual-event.component.scss']
})
export class VirtualEventComponent implements OnInit {
  /**
   * Creates an instance of VirtualEventComponent.
   *
   * @param router - Angular Router service for navigation.
   * @param loaderService - Service to control loading indicators.
   * @param profileBllService - Business logic layer service for user profiles.
   * @param userService - Service for user-related operations.
   */
  constructor(
    private router: Router,
    private loaderService: LoaderService,
    private profileBllService: ProfileBllService,
    private userService: UserService
  ) { }

  /**
   * Stores the user's profile information for the virtual event.
   *
   * This property is initialized as `null` and is expected to be populated with
   * the `user_profile` data from the `UserEventStatusRespModel` interface.
   */
  userProfile: UserEventStatusRespModel["user_profile"] | null = null;

  /**
   * Angular lifecycle hook that is called after the component's view has been initialized.
   * Asynchronously fetches the user's event status when the component is initialized.
   *
   * @returns {Promise<void>} A promise that resolves when the user event status has been fetched.
   */
  async ngOnInit(): Promise<void> {
    await this.fetchUserEventStatus();
  }

  /**
   * Indicates whether the loader is currently shown.
   * Returns `true` if the loader service is active, otherwise `false`.
   */
  get isLoading(): boolean {
    return this.loaderService.isShown;
  }

  /**
   * Fetches the current user's event status asynchronously and updates the user profile information.
   *
   * This method displays a loader while fetching data, retrieves the user's event status from the backend,
   * and updates the user service with the user's profile details such as name, profile image, mobile, state, and city.
   * If an error occurs during the fetch, it logs the error and displays an alert to the user.
   * The loader is hidden after the operation completes, regardless of success or failure.
   *
   * @returns {Promise<void>} A promise that resolves when the user event status has been fetched and processed.
   */
  private async fetchUserEventStatus(): Promise<void> {
    this.loaderService.show();
    try {
      const res: BaseResponse<UserEventStatusRespModel> = await this.profileBllService.submitGetUserEventStatus();
      this.userProfile = res.data.user_profile;

      if (this.userProfile) {
        const previewLogo =
          this.userProfile.image_url ??
          "/assets/images/Default-profile-photo.png";
        const userName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;
        this.userService.setUser({
          name: userName,
          profileImg: previewLogo,
          mobile: this.userProfile.mobile,
          state: this.userProfile.state,
          city: this.userProfile.city,
          hasUnassignedTickets: res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
          hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,

        });
      }
    } catch (err) {
      console.error("Error fetching user event status:", err);
      alert("Failed to fetch user event status. Please try again.");
    } finally {
      this.loaderService.hide();
    }
  }


  /**
   * Navigates the user back to the "home" route, replacing the current URL in the browser history.
   * This prevents the user from returning to the previous page using the browser's back button.
   */
  goBack(): void {
    this.router.navigate(["home"], { replaceUrl: true });
  }

  /**
   * Navigates the user to the digital catalog pavilion list page.
   *
   * This method uses Angular's Router to change the current route to
   * "catalogue/pavilion-list", displaying the digital catalog to the user.
   */
  gotoDigitalCatalog(): void {
    this.router.navigate(["catalogue/pavilion-list"]);
  }

}
